












































































import {
  defineComponent,
  ref,
  computed,
  ComputedRef,
  watch,
} from '@vue/composition-api';
import { v4 } from 'uuid';
import {
  EpigraphicUnitSide,
  ColumnContent,
  SideContent,
  AddTextEditorContent,
} from '@oare/types';
import Side from './components/Side.vue';
import AddSide from './components/AddSide.vue';
import SideCard from './components/SideCard.vue';
import { convertSideToSideNumber } from '@oare/oare';

export interface Side {
  uuid: string;
  side: EpigraphicUnitSide;
  lastLine: number;
  breaks: number;
  endsBroken: boolean;
  isDirty: boolean;
  columns: ColumnContent[];
}

export default defineComponent({
  components: {
    Side,
    AddSide,
    SideCard,
  },
  setup(_, { emit }) {
    const indexToChange = ref(0);
    const sides = ref<Side[]>([]);
    const selectedSide = ref<EpigraphicUnitSide>();

    const addingSide = ref(false);
    const changingSide = ref(false);
    const removingSide = ref(false);

    const sideTypes: ComputedRef<EpigraphicUnitSide[]> = computed(() => [
      'obv.',
      'lo.e.',
      'rev.',
      'u.e.',
      'le.e.',
      'r.e.',
      'mirror text',
      'legend',
      'suppl. tablet',
      'obv. ii',
    ]);

    const usableSides = computed(() =>
      sideTypes.value.filter(
        type => !sides.value.map(side => side.side).includes(type)
      )
    );

    const sortSides = (a: Side, b: Side) => {
      if (a.side === 'obv.') {
        return -1;
      }
      if (a.side === 'lo.e.') {
        if (b.side === 'obv.') {
          return 1;
        }
        return -1;
      }
      if (a.side === 'rev.') {
        if (b.side === 'obv.' || b.side === 'lo.e.') {
          return 1;
        }
        return -1;
      }
      if (a.side === 'u.e.') {
        if (b.side === 'obv.' || b.side === 'lo.e.' || b.side === 'rev.') {
          return 1;
        }
        return -1;
      }
      if (a.side === 'le.e.') {
        if (
          b.side === 'obv.' ||
          b.side === 'lo.e.' ||
          b.side === 'rev.' ||
          b.side === 'u.e.'
        ) {
          return 1;
        }
        return -1;
      }
      if (a.side === 'r.e.') {
        if (
          b.side === 'mirror text' ||
          b.side === 'legend' ||
          b.side === 'suppl. tablet' ||
          b.side === 'obv. ii'
        ) {
          return -1;
        }
        return 1;
      }
      if (a.side === 'mirror text') {
        if (
          b.side === 'legend' ||
          b.side === 'suppl. tablet' ||
          b.side === 'obv. ii'
        ) {
          return -1;
        }
        return 1;
      }
      if (a.side === 'legend') {
        if (b.side === 'suppl. tablet' || b.side === 'obv. ii') {
          return -1;
        }
        return 1;
      }
      if (a.side === 'suppl. tablet') {
        if (b.side === 'obv. ii') {
          return -1;
        }
        return 1;
      }
      if (a.side === 'obv. ii') {
        return 1;
      }
      return 0;
    };

    const sortedSides = computed(() => {
      return sides.value.sort(sortSides);
    });

    const openAddingSide = () => {
      addingSide.value = true;
      selectedSide.value = undefined;
    };

    const addSide = (side: EpigraphicUnitSide) => {
      sides.value.push({
        uuid: v4(),
        side,
        lastLine: 0,
        breaks: 0,
        endsBroken: false,
        isDirty: false,
        columns: [],
      });
      addingSide.value = false;
      selectedSide.value = side;
    };

    const removeSide = (index: number) => {
      removingSide.value = true;
      sides.value.splice(index, 1);
      if (sortedSides.value.length > 0) {
        selectedSide.value = sortedSides.value[0].side;
      } else {
        selectedSide.value = undefined;
      }
    };

    const setSide = (side: EpigraphicUnitSide) => {
      if (!changingSide.value && !removingSide.value) {
        addingSide.value = false;
        selectedSide.value = side;
      }
      removingSide.value = false;
    };

    const setLastLine = (index: number, lastLine: number) => {
      sides.value[index].lastLine = lastLine;
    };

    const getStartingLine = (index: number) => {
      if (index === 0) {
        return 0;
      }
      let startingLine = 0;
      for (let i = index - 1; i >= 0 && !startingLine; i -= 1) {
        if (sides.value[i].lastLine !== 0) {
          startingLine = sides.value[i].lastLine;
        }
      }
      return Math.floor(startingLine);
    };

    const setBrokenArea = (index: number, breaks: number) => {
      sides.value[index].breaks = breaks;
    };

    const getStartingBreak = (index: number) => {
      if (index === 0) {
        return 0;
      }
      let startingBreak = 0;
      for (let i = index - 1; i >= 0; i -= 1) {
        if (sides.value[i].breaks !== 0) {
          startingBreak += sides.value[i].breaks;
        }
      }
      return startingBreak;
    };

    const setEndBreakStatus = (index: number, status: boolean) => {
      sides.value[index].endsBroken = status;
    };

    const getBeginningBreakStatus = (index: number) => {
      if (index === 0) {
        return false;
      }
      let startingBreak = false;
      let foundDirty = false;

      for (let i = index - 1; i >= 0 && !startingBreak && !foundDirty; i -= 1) {
        if (sides.value[i].isDirty) {
          foundDirty = true;
        }
        if (sides.value[i].endsBroken) {
          startingBreak = true;
        }
      }
      return startingBreak;
    };

    const setDirtyStatus = (index: number, status: boolean) => {
      sides.value[index].isDirty = status;
    };

    const setupChangeSide = (index: number) => {
      changingSide.value = true;
      indexToChange.value = index;
    };

    const changeSide = (side: EpigraphicUnitSide) => {
      sides.value[indexToChange.value].side = side;
      changingSide.value = false;
      selectedSide.value = side;
    };

    const setSideColumns = (index: number, columns: ColumnContent[]) => {
      sides.value.splice(index, 1, {
        ...sides.value[index],
        columns,
      });
    };

    watch(
      sides,
      () => {
        const sideContent: SideContent[] = sides.value.map(side => ({
          uuid: side.uuid,
          type: side.side,
          number: convertSideToSideNumber(side.side),
          columns: side.columns,
        }));
        const editorContent: AddTextEditorContent = {
          sides: sideContent,
        };
        emit('update-editor-content', editorContent);
      },
      { deep: true }
    );

    const stepComplete = computed(() => {
      return (
        sides.value.length > 0 &&
        sides.value.every(
          side =>
            side.columns.length > 0 &&
            side.columns.every(
              column =>
                column.rows.length > 0 &&
                column.rows.every(row => {
                  if (row.type === 'Line') {
                    return (
                      row.signs &&
                      row.signs.length > 0 &&
                      row.signs.every(sign => sign.type)
                    );
                  }
                  return true;
                }) &&
                column.rows.every(row => !row.hasErrors)
            )
        )
      );
    });

    watch(stepComplete, () => emit('step-complete', stepComplete.value));

    const currentSide = ref<string>();
    const setCurrentSide = (sideUuid?: string) => {
      currentSide.value = sideUuid;
    };

    return {
      sides,
      addingSide,
      removingSide,
      usableSides,
      addSide,
      removeSide,
      selectedSide,
      setSide,
      openAddingSide,
      sortedSides,
      setLastLine,
      getStartingLine,
      setBrokenArea,
      getStartingBreak,
      setEndBreakStatus,
      getBeginningBreakStatus,
      setDirtyStatus,
      changingSide,
      setupChangeSide,
      changeSide,
      setSideColumns,
      currentSide,
      setCurrentSide,
    };
  },
});
